export interface ComputedDiffWrite {
  index: number;
  expected?: any;
  actual?: any;
  expectedPreview?: any;
  actualPreview?: any;
  expectedHash?: string | null;
  actualHash?: string | null;
  expectedBytes?: number;
  actualBytes?: number;
  diffPaths?: Array<Array<string | number>>;
  isMismatched: boolean;
  actionName?: string;
  fetchName?: string;
  stepId?: string | null;
  stepName?: string;
  stepType?: string;
}

export interface ComputedDiffGroup {
  stepId?: string | null;
  stepName: string;
  stepType?: string;
  writes: ComputedDiffWrite[];
  hasMismatch: boolean;
}

export interface ComputedDiffPayload {
  version?: number;
  expectedSummary: any;
  actualSummary: any;
  hasSummaryMismatch: boolean;
  mismatchCount: number;
  maxWritesLen: number;
  groups: ComputedDiffGroup[];
}

const MAX_COMPUTED_DIFF_JSON_BYTES = 1024 * 1024;
const MAX_COMPUTED_DIFF_GROUPS = 100;
const MAX_COMPUTED_DIFF_WRITES = 500;
const MAX_DISPLAY_VALUE_DEPTH = 8;
const MAX_DISPLAY_VALUE_KEYS = 40;
const MAX_DISPLAY_VALUE_ITEMS = 40;
const MAX_DISPLAY_STRING_LENGTH = 4096;
const MAX_DISPLAY_TOTAL_NODES = 5000;
const MAX_DISPLAY_TOTAL_STRING_LENGTH = 128 * 1024;

interface DisplayBudget {
  remainingNodes: number;
  remainingStringLength: number;
}

function getUtf8ByteLength(value: string): number {
  let bytes = 0;
  for (let index = 0; index < value.length; index++) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit <= 0x7f) {
      bytes += 1;
    } else if (codeUnit <= 0x7ff) {
      bytes += 2;
    } else if (codeUnit >= 0xd800 && codeUnit <= 0xdbff
      && index + 1 < value.length
      && value.charCodeAt(index + 1) >= 0xdc00
      && value.charCodeAt(index + 1) <= 0xdfff) {
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

function createDisplayBudget(): DisplayBudget {
  return {
    remainingNodes: MAX_DISPLAY_TOTAL_NODES,
    remainingStringLength: MAX_DISPLAY_TOTAL_STRING_LENGTH,
  };
}

function isRecord(value: any): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function isPrivateSnapshotKey(key: unknown): boolean {
  return typeof key === 'string' && key.startsWith('@_');
}

export function stripPrivateSnapshotFields(value: any): any {
  if (!value || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => stripPrivateSnapshotFields(item));
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return value;
  }

  const cleaned: Record<string, any> = {};
  for (const key of Object.keys(value)) {
    if (!isPrivateSnapshotKey(key)) {
      cleaned[key] = stripPrivateSnapshotFields(value[key]);
    }
  }
  return cleaned;
}

function isNonNegativeInteger(value: any): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && Math.floor(value) === value;
}

function isOptionalString(value: any): boolean {
  return typeof value === 'undefined' || typeof value === 'string';
}

function isOptionalNullableString(value: any): boolean {
  return value === null || isOptionalString(value);
}

function isDiffPath(value: any): boolean {
  return Array.isArray(value) && value.every(segment => typeof segment === 'string' || isNonNegativeInteger(segment));
}

function isComputedDiffWrite(value: any): value is ComputedDiffWrite {
  return isRecord(value)
    && isNonNegativeInteger(value.index)
    && typeof value.isMismatched === 'boolean'
    && (typeof value.expectedHash === 'undefined' || value.expectedHash === null || typeof value.expectedHash === 'string')
    && (typeof value.actualHash === 'undefined' || value.actualHash === null || typeof value.actualHash === 'string')
    && (typeof value.expectedBytes === 'undefined' || isNonNegativeInteger(value.expectedBytes))
    && (typeof value.actualBytes === 'undefined' || isNonNegativeInteger(value.actualBytes))
    && isOptionalString(value.actionName)
    && isOptionalString(value.fetchName)
    && isOptionalNullableString(value.stepId)
    && isOptionalString(value.stepName)
    && isOptionalString(value.stepType)
    && (typeof value.diffPaths === 'undefined'
      || (Array.isArray(value.diffPaths) && value.diffPaths.every(isDiffPath)));
}

function isComputedDiffGroup(value: any): value is ComputedDiffGroup {
  return isRecord(value)
    && isOptionalNullableString(value.stepId)
    && typeof value.stepName === 'string'
    && isOptionalString(value.stepType)
    && typeof value.hasMismatch === 'boolean'
    && Array.isArray(value.writes)
    && value.writes.every(isComputedDiffWrite);
}

function boundDisplayValue(
  value: any,
  budget: DisplayBudget = createDisplayBudget(),
  depth: number = 0,
  ancestors: any[] = [],
): any {
  if (budget.remainingNodes <= 0) {
    return { __snapshotDiffTruncated: true, type: 'budget' };
  }
  budget.remainingNodes -= 1;

  if (typeof value === 'string') {
    const retainedLength = Math.min(value.length, MAX_DISPLAY_STRING_LENGTH, budget.remainingStringLength);
    budget.remainingStringLength -= retainedLength;
    if (retainedLength === value.length) {
      return value;
    }
    return {
      __snapshotDiffTruncated: true,
      type: 'string',
      originalLength: value.length,
      preview: value.slice(0, retainedLength),
    };
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  if (ancestors.indexOf(value) !== -1) {
    return { __snapshotDiffTruncated: true, type: 'circular' };
  }

  if (depth >= MAX_DISPLAY_VALUE_DEPTH) {
    return Array.isArray(value)
      ? { __snapshotDiffTruncated: true, type: 'array', length: value.length }
      : { __snapshotDiffTruncated: true, type: 'object', keyCount: Object.keys(value).length };
  }

  const nextAncestors = ancestors.concat([value]);
  if (Array.isArray(value)) {
    const result = value.slice(0, MAX_DISPLAY_VALUE_ITEMS)
      .map(item => boundDisplayValue(item, budget, depth + 1, nextAncestors));
    if (result.length < value.length) {
      result.push({ __snapshotDiffOmittedItems: value.length - result.length });
    }
    return result;
  }

  const keys = Object.keys(value);
  const result: Record<string, any> = {};
  for (const key of keys.slice(0, MAX_DISPLAY_VALUE_KEYS)) {
    result[key] = boundDisplayValue(value[key], budget, depth + 1, nextAncestors);
  }
  if (keys.length > MAX_DISPLAY_VALUE_KEYS) {
    result.__snapshotDiffOmittedKeys = keys.length - MAX_DISPLAY_VALUE_KEYS;
  }
  return result;
}

function boundComputedDiffWrite(write: ComputedDiffWrite, budget: DisplayBudget): ComputedDiffWrite {
  const bounded: ComputedDiffWrite = {
    index: write.index,
    isMismatched: write.isMismatched,
  };
  const scalarKeys: Array<keyof ComputedDiffWrite> = [
    'expectedHash', 'actualHash', 'expectedBytes', 'actualBytes', 'diffPaths',
    'actionName', 'fetchName', 'stepId', 'stepName', 'stepType',
  ];
  for (const key of scalarKeys) {
    if (typeof write[key] !== 'undefined') {
      (bounded as any)[key] = write[key];
    }
  }

  if (typeof write.expectedPreview !== 'undefined') {
    bounded.expectedPreview = boundDisplayValue(write.expectedPreview, budget);
  } else if (typeof write.expected !== 'undefined') {
    bounded.expected = boundDisplayValue(write.expected, budget);
  }
  if (typeof write.actualPreview !== 'undefined') {
    bounded.actualPreview = boundDisplayValue(write.actualPreview, budget);
  } else if (typeof write.actual !== 'undefined') {
    bounded.actual = boundDisplayValue(write.actual, budget);
  }
  return bounded;
}

function normalizeComputedDiffPayload(parsed: any): ComputedDiffPayload | null {
  if (!isRecord(parsed)
    || !Array.isArray(parsed.groups)
    || parsed.groups.length > MAX_COMPUTED_DIFF_GROUPS
    || !parsed.groups.every(isComputedDiffGroup)
    || (typeof parsed.version !== 'undefined' && !isNonNegativeInteger(parsed.version))
    || typeof parsed.hasSummaryMismatch !== 'boolean'
    || !isNonNegativeInteger(parsed.mismatchCount)
    || !isNonNegativeInteger(parsed.maxWritesLen)) {
    return null;
  }

  const writeCount = parsed.groups.reduce((count: number, group: ComputedDiffGroup) => count + group.writes.length, 0);
  if (writeCount > MAX_COMPUTED_DIFF_WRITES) {
    return null;
  }

  const displayBudget = createDisplayBudget();
  const normalized: ComputedDiffPayload = {
    expectedSummary: boundDisplayValue(parsed.expectedSummary, displayBudget),
    actualSummary: boundDisplayValue(parsed.actualSummary, displayBudget),
    hasSummaryMismatch: parsed.hasSummaryMismatch,
    mismatchCount: parsed.mismatchCount,
    maxWritesLen: parsed.maxWritesLen,
    groups: parsed.groups.map((group: ComputedDiffGroup) => ({
      stepId: group.stepId,
      stepName: group.stepName,
      stepType: group.stepType,
      hasMismatch: group.hasMismatch,
      writes: group.writes.map((write: ComputedDiffWrite) => boundComputedDiffWrite(write, displayBudget)),
    })),
  };
  if (typeof parsed.version !== 'undefined') {
    normalized.version = parsed.version;
  }

  try {
    return getUtf8ByteLength(JSON.stringify(normalized)) <= MAX_COMPUTED_DIFF_JSON_BYTES ? normalized : null;
  } catch (e) {
    return null;
  }
}

export function parseComputedDiffPayload(computedDiff: any): ComputedDiffPayload | null {
  if (!computedDiff) {
    return null;
  }

  if (typeof computedDiff === 'string' && getUtf8ByteLength(computedDiff) > MAX_COMPUTED_DIFF_JSON_BYTES) {
    return null;
  }

  const parsed = typeof computedDiff === 'string'
    ? (() => {
      try {
        return JSON.parse(computedDiff);
      } catch (e) {
        return null;
      }
    })()
    : computedDiff;

  return normalizeComputedDiffPayload(parsed);
}

export function parseSnapshotOutputPayload(payload: any): any {
  if (typeof payload !== 'string') {
    return payload;
  }

  try {
    return JSON.parse(payload);
  } catch (e) {
    return payload;
  }
}

function sortJsonValue(value: any): any {
  if (Array.isArray(value)) {
    return value.map(sortJsonValue);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, any>>((result, key) => {
        result[key] = sortJsonValue(value[key]);
        return result;
      }, {});
  }
  return value;
}

function normalizeErrorForDisplay(error: string): string {
  const bodyMarker = /\bbody\s+/i.exec(error);
  if (!bodyMarker) {
    return error;
  }

  const bodyStart = bodyMarker.index + bodyMarker[0].length;
  const body = error.slice(bodyStart).trim();
  if (!body.startsWith('{') && !body.startsWith('[')) {
    return error;
  }

  try {
    const parsedBody = JSON.parse(body);
    return `${error.slice(0, bodyStart)}${JSON.stringify(sortJsonValue(parsedBody))}`.trim();
  } catch (e) {
    return error;
  }
}

export function cleanSnapshotOutputForDisplay(value: any, currentKey?: string, depth: number = 0): any {
  if (currentKey === 'error' && typeof value === 'string') {
    return normalizeErrorForDisplay(value);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => cleanSnapshotOutputForDisplay(item, currentKey, depth + 1));
  }

  const cleaned: Record<string, any> = {};
  for (const key of Object.keys(value)) {
    if (isPrivateSnapshotKey(key) || (depth === 0 && (
      key === 'fetchLog'
      || key === 'headers'
      || key === 'requestHeaders'
      || key === 'responseHeaders'
      || key === 'status'
      || key === 'channelWrites'
      || key === 'executedSteps'
      || key === 'replayWriteAttempts'
    ))) {
      continue;
    }
    cleaned[key] = cleanSnapshotOutputForDisplay(value[key], key, depth + 1);
  }

  return cleaned;
}

function areJsonValuesSemanticallyEqual(expected: any, actual: any, seen: Array<[any, any]> = []): boolean {
  if (expected === actual) {
    return true;
  }
  if (!expected || !actual || typeof expected !== 'object' || typeof actual !== 'object') {
    return Number.isNaN(expected) && Number.isNaN(actual);
  }
  if (Array.isArray(expected) || Array.isArray(actual)) {
    return Array.isArray(expected)
      && Array.isArray(actual)
      && expected.length === actual.length
      && expected.every((value, index) => areJsonValuesSemanticallyEqual(value, actual[index], seen));
  }
  if (seen.some(pair => pair[0] === expected && pair[1] === actual)) {
    return true;
  }

  const expectedKeys = Object.keys(expected).sort();
  const actualKeys = Object.keys(actual).sort();
  if (expectedKeys.length !== actualKeys.length
    || expectedKeys.some((key, index) => key !== actualKeys[index])) {
    return false;
  }

  const nextSeen = seen.concat([[expected, actual]] as Array<[any, any]>);
  return expectedKeys.every(key => areJsonValuesSemanticallyEqual(expected[key], actual[key], nextSeen));
}

export function buildDisplayComputedDiffPayload(testInvocation?: { expectedOutput?: any } | null, lastRun?: { actualOutput?: any; computedDiff?: any } | null): ComputedDiffPayload | null {
  const computedDiffPayload = parseComputedDiffPayload(lastRun?.computedDiff);

  if (computedDiffPayload && (computedDiffPayload.groups.length > 0 || computedDiffPayload.hasSummaryMismatch || computedDiffPayload.maxWritesLen > 0 || computedDiffPayload.mismatchCount > 0)) {
    return cleanSnapshotOutputForDisplay(computedDiffPayload);
  }

  const expectedSummary = cleanSnapshotOutputForDisplay(parseSnapshotOutputPayload(testInvocation?.expectedOutput));
  const actualSummary = cleanSnapshotOutputForDisplay(parseSnapshotOutputPayload(lastRun?.actualOutput));

  if (typeof actualSummary === 'undefined') {
    return computedDiffPayload;
  }

  const comparableExpectedSummary = typeof expectedSummary === 'undefined' ? null : expectedSummary;
  const comparableActualSummary = typeof actualSummary === 'undefined' ? null : actualSummary;
  if (areJsonValuesSemanticallyEqual(comparableExpectedSummary, comparableActualSummary)) {
    return computedDiffPayload;
  }

  const displayBudget = createDisplayBudget();
  return {
    version: computedDiffPayload?.version,
    expectedSummary: boundDisplayValue(expectedSummary, displayBudget),
    actualSummary: boundDisplayValue(actualSummary, displayBudget),
    hasSummaryMismatch: true,
    mismatchCount: computedDiffPayload?.mismatchCount || 1,
    maxWritesLen: computedDiffPayload?.maxWritesLen || 0,
    groups: computedDiffPayload?.groups || [],
  };
}
